import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import type { DiscountCodeNameState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

export const DiscountCodeNameDetails = memo(function DiscountCodeNameDetails({
  stepSelect,
  state,
  setState,
}: StepTypeDetailsProps<DiscountCodeNameState>) {
  const { t } = useTranslation();
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const nextStepChange = useHandler((next: StepId | undefined) => {
    setState((state) => ({ ...state, next }));
  });
  const handlePrefixChange = useHandler((prefix: string) => {
    setState((state) => ({ ...state, prefix }));
  });
  const handleSuffixChange = useHandler((suffix: string) => {
    setState((state) => ({ ...state, suffix }));
  });

  return (
    <>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Discount code prefix")}
        </Text>
        <RedoTextInput
          placeholder={t("Enter prefix...")}
          setValue={handlePrefixChange}
          state={canEditReturnsSettings ? "default" : "readonly"}
          value={state.prefix}
        />
        <Text fontSize="xxs" fontWeight="regular" textColor="secondary">
          {t("Prefix that will be prepended to any created discount codes")}
        </Text>
      </Flex>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Discount code suffix")}
        </Text>
        <RedoTextInput
          placeholder={t("Enter suffix...")}
          setValue={handleSuffixChange}
          state={canEditReturnsSettings ? "default" : "readonly"}
          value={state.suffix}
        />
        <Text fontSize="xxs" fontWeight="regular" textColor="secondary">
          {t("Suffix that will be appended to any created discount codes")}
        </Text>
      </Flex>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Next step")}
        </Text>
        {stepSelect({ value: state.next, valueChange: nextStepChange })}
      </Flex>
    </>
  );
});
